// Self-serve Free key: one per email, redeemed online like every other key.

import { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { api } from '../services/api';
import { LicenseKeyDisplay } from './KeyDisplay';
import type { FreeKey } from '../types';

export function FreeKeyCard({ freeKey, onIssued }: { freeKey: FreeKey | null; onIssued: () => void }) {
  const [isIssuing, setIsIssuing] = useState(false);
  const [error, setError] = useState('');

  const handleGetKey = async () => {
    setIsIssuing(true);
    setError('');
    try {
      await api.getFreeKey();
      onIssued();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get a free key');
    } finally {
      setIsIssuing(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-accent uppercase tracking-wider">Free key</p>
          <p className="text-sm text-muted-foreground mt-1">
            Set it as <span className="font-mono">DIOSC_LICENSE</span> on your dioschub instance. Without a key,
            dioschub runs as bare chat only.
          </p>
        </div>
        {!freeKey && (
          <button
            onClick={handleGetKey}
            disabled={isIssuing}
            className="inline-flex items-center gap-2 bg-accent text-white font-medium px-4 py-2 rounded-md hover:bg-accent/90 transition-all text-sm disabled:opacity-50 shrink-0"
          >
            <KeyRound className="w-4 h-4" />
            {isIssuing ? 'Getting key...' : 'Get a free key'}
          </button>
        )}
      </div>
      {freeKey && (
        <>
          <LicenseKeyDisplay licenseKey={freeKey.key} />
          <p className="text-xs text-muted-foreground mt-2">
            {freeKey.activated
              ? `Activated on instance ${freeKey.instanceId ?? 'unknown'}${
                  freeKey.lastRefreshAt ? ` · last refresh ${new Date(freeKey.lastRefreshAt).toLocaleString()}` : ''
                }`
              : 'Not activated yet — it binds to the first instance that redeems it.'}
          </p>
        </>
      )}
      {error && <p className="text-sm text-destructive mt-2">{error}</p>}
    </div>
  );
}
